import React from "react";
import { FiAlertTriangle, FiCheckCircle, FiInfo, FiX } from "react-icons/fi";
import { Button } from "./Button";

export type ResultDialogTone = "success" | "error" | "info" | "warning";

export interface ResultDialogAction {
  label: string;
  onClick: () => void;
}

export interface ResultDialogProps {
  open: boolean;
  title: string;
  description?: React.ReactNode;
  tone?: ResultDialogTone;
  primaryAction?: ResultDialogAction;
  secondaryAction?: ResultDialogAction;
  closeLabel?: string;
  onClose: () => void;
}

const toneStyles: Record<
  ResultDialogTone,
  {
    icon: string;
    button: "primary" | "danger" | "success" | "warning";
    iconNode: React.ReactNode;
  }
> = {
  success: {
    icon: "bg-accent-50 text-accent-700",
    button: "success",
    iconNode: <FiCheckCircle />,
  },
  error: {
    icon: "bg-rose-50 text-rose-700",
    button: "danger",
    iconNode: <FiAlertTriangle />,
  },
  info: {
    icon: "bg-sky-50 text-sky-700",
    button: "primary",
    iconNode: <FiInfo />,
  },
  warning: {
    icon: "bg-amber-50 text-amber-700",
    button: "warning",
    iconNode: <FiAlertTriangle />,
  },
};

export function ResultDialog({
  open,
  title,
  description,
  tone = "success",
  primaryAction,
  secondaryAction,
  closeLabel = "Fechar",
  onClose,
}: ResultDialogProps) {
  const titleId = React.useId();
  const descriptionId = React.useId();

  if (!open) return null;

  const styles = toneStyles[tone];

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/35 p-0 backdrop-blur-[1px] sm:items-center sm:p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className="max-h-[calc(100dvh-1rem)] w-full overflow-y-auto rounded-t-3xl border border-slate-200 bg-white p-5 shadow-2xl sm:max-h-[calc(100dvh-3rem)] sm:max-w-md sm:rounded-2xl sm:p-6"
      >
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
          >
            <FiX />
          </button>
        </div>

        <div className="text-center">
          <div
            className={`mx-auto flex h-14 w-14 items-center justify-center rounded-2xl text-2xl ${styles.icon}`}
            aria-hidden="true"
          >
            {styles.iconNode}
          </div>
          <h2 id={titleId} className="mt-4 text-xl font-bold text-slate-950">
            {title}
          </h2>
          {description ? (
            <div
              id={descriptionId}
              className="mt-2 text-sm font-medium leading-6 text-slate-500"
            >
              {description}
            </div>
          ) : null}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {secondaryAction ? (
            <Button
              type="button"
              variant="outline"
              onClick={secondaryAction.onClick}
              fullWidth
            >
              {secondaryAction.label}
            </Button>
          ) : null}
          <Button
            type="button"
            variant={primaryAction ? styles.button : "outline"}
            onClick={primaryAction?.onClick ?? onClose}
            fullWidth
            className={!secondaryAction ? "sm:col-span-2" : ""}
          >
            {primaryAction?.label ?? closeLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
